/**
 * OrdersPage — modulo Ordini attrezzatura.
 *
 * SM/AM creano ordini per i negozi nel proprio perimetro, OPS approva,
 * rifiuta e segna consegnato (con carico automatico Magazzino per i
 * prodotti mappati a una categoria inventario).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePermissions, Can } from '../lib/usePermissions';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ShoppingCart, Plus, Loader2, Inbox, Mail, FileDown, Trash2, X, FilterX, BarChart3, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NewOrderDialog from '../components/NewOrderDialog';
import OrderDetailDialog from '../components/OrderDetailDialog';
import EmailOrdersDialog from '../components/EmailOrdersDialog';
import OrdersSpendingReport from '../components/OrdersSpendingReport';
import { toast } from 'sonner';

export const ORDER_STATUS_META = {
  pending: { label: 'In attesa', cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  approved: { label: 'Approvato', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  rejected: { label: 'Rifiutato', cls: 'bg-red-50 text-red-700 border-red-300' },
  partially_delivered: { label: 'Consegnato parzialmente', cls: 'bg-cyan-50 text-cyan-700 border-cyan-300' },
  delivered: { label: 'Consegnato', cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  cancelled: { label: 'Annullato', cls: 'bg-slate-100 text-slate-500 border-slate-300' },
};

const FILTERS = [
  { value: '', label: 'Tutti' },
  { value: 'pending', label: 'In attesa' },
  { value: 'approved', label: 'Approvati' },
  { value: 'partially_delivered', label: 'Consegnati parz.' },
  { value: 'delivered', label: 'Consegnati' },
  { value: 'rejected', label: 'Rifiutati' },
  { value: 'cancelled', label: 'Annullati' },
];

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtPrice(v) {
  if (v == null) return '—';
  return `€ ${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Soglia "consegna in ritardo": un ordine approvato da più di N giorni e mai
// arrivato viene segnalato in cima alla pagina e con un badge sulla riga.
export const OVERDUE_DAYS = 15;

export function daysSince(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((Date.now() - t) / 86400000);
}

export function overdueDays(order) {
  if (!order || order.status !== 'approved') return 0;
  const d = daysSince(order.decided_at || order.created_at);
  return d >= OVERDUE_DAYS ? d : 0;
}

export default function OrdersPage() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [orders, setOrders] = useState(null);
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [showEmail, setShowEmail] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const canBulk = has('orders.approve');
  const isOps = user?.role === 'operations_manager';

  const load = useCallback(async () => {
    try {
      const res = await api.get('/orders');
      setOrders(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento ordini');
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    load();
    api.get('/stores').then(r => setStores(r.data || [])).catch(() => {});
  }, [load]);

  const filtered = useMemo(
    () => (orders || []).filter(o => {
      if (filter && o.status !== filter) return false;
      if (storeFilter && o.store_id !== storeFilter) return false;
      const day = (o.created_at || '').slice(0, 10);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    }),
    [orders, filter, storeFilter, dateFrom, dateTo]
  );

  const hasExtraFilters = storeFilter || dateFrom || dateTo;

  const pendingCount = useMemo(
    () => (orders || []).filter(o => o.status === 'pending').length,
    [orders]
  );

  // Ordini approvati da oltre OVERDUE_DAYS giorni e mai arrivati.
  const overdue = useMemo(
    () => (orders || [])
      .map(o => ({ ...o, waiting: overdueDays(o) }))
      .filter(o => o.waiting > 0)
      .sort((a, b) => b.waiting - a.waiting),
    [orders]
  );

  const toggleCheck = (id) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const allFilteredChecked = filtered.length > 0 && filtered.every(o => checked.has(o.order_id));
  const toggleAll = () => setChecked(allFilteredChecked ? new Set() : new Set(filtered.map(o => o.order_id)));
  const checkedIds = [...checked];

  const downloadPdf = async () => {
    setBusyPdf(true);
    try {
      const r = await api.post('/orders/summary-pdf', { order_ids: checkedIds }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ordini_riepilogo_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download PDF fallito');
    } finally {
      setBusyPdf(false);
    }
  };

  const bulkDelete = async () => {
    try {
      const r = await api.post('/orders/bulk-delete', { order_ids: checkedIds });
      toast.success(`${r.data.deleted} ordini eliminati`);
      setConfirmDelete(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione fallita');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1920px] space-y-6" data-testid="orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingCart className="w-6 h-6" /> Ordini
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ordini di attrezzatura per i negozi
            {has('orders.approve') && pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300" data-testid="orders-pending-badge">
                {pendingCount} da approvare
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowReport(true)} className="gap-1.5" data-testid="spending-report-btn">
            <BarChart3 className="w-4 h-4" /> Report spese
          </Button>
          <Can permission="orders.create">
            <Button onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-order-btn">
              <Plus className="w-4 h-4" /> Nuovo ordine
            </Button>
          </Can>
        </div>
      </div>

      {overdue.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="orders-overdue-banner">
          <div className="flex flex-wrap items-center gap-2">
            <Clock className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-sm text-amber-900 font-semibold" data-testid="orders-overdue-count">
              {overdue.length === 1
                ? `1 ordine approvato da più di ${OVERDUE_DAYS} giorni non è ancora arrivato`
                : `${overdue.length} ordini approvati da più di ${OVERDUE_DAYS} giorni non sono ancora arrivati`}
            </p>
            <Button variant="outline" size="sm" className="h-7 rounded-none text-xs ml-auto border-amber-400 text-amber-800 hover:bg-amber-100"
                    onClick={() => { setFilter('approved'); setStoreFilter(''); setDateFrom(''); setDateTo(''); }}
                    data-testid="orders-overdue-filter-btn">
              Vedi gli approvati
            </Button>
          </div>
          <div className="divide-y divide-amber-200 border-t border-amber-200">
            {overdue.slice(0, 5).map(o => (
              <button key={o.order_id} onClick={() => setSelected(o)}
                      className="w-full flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5 text-left text-xs text-amber-900 hover:bg-amber-100/70 transition-colors"
                      data-testid={`orders-overdue-item-${o.order_id}`}>
                <span className="font-mono font-semibold w-20">{o.number}</span>
                <span className="min-w-0 flex-1 truncate">{o.store_name}</span>
                <span className="hidden sm:block">{o.items_count} art.</span>
                <span className="font-semibold">in attesa da {o.waiting} giorni</span>
              </button>
            ))}
          </div>
          {overdue.length > 5 && (
            <p className="text-[11px] text-amber-700">…e altri {overdue.length - 5}. Usa il filtro “Approvati” per vederli tutti.</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5" data-testid="orders-filters">
        {FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`orders-filter-${f.value || 'all'}`}
            className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
              filter === f.value
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'
            }`}
          >
            {f.label}
          </button>
        ))}
        {canBulk && filtered.length > 0 && (
          <label className="ml-auto flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none px-2 py-1.5">
            <input type="checkbox" checked={allFilteredChecked} onChange={toggleAll}
                   className="w-4 h-4 accent-slate-900" data-testid="orders-select-all" />
            Seleziona tutti
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="orders-extra-filters">
        <Select value={storeFilter || 'all'} onValueChange={v => setStoreFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="rounded-none h-9 w-[210px] bg-white text-sm" data-testid="orders-store-filter">
            <SelectValue placeholder="Tutti i negozi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i negozi</SelectItem>
            {[...stores].sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', 'it')).map(s => (
              <SelectItem key={s.store_id} value={s.store_id} data-testid={`orders-store-filter-opt-${s.store_id}`}>
                {s.display_name || s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <span>Dal</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                 className="h-9 px-2 border border-slate-300 bg-white text-sm text-slate-700"
                 data-testid="orders-date-from" />
          <span>al</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                 className="h-9 px-2 border border-slate-300 bg-white text-sm text-slate-700"
                 data-testid="orders-date-to" />
        </div>
        {hasExtraFilters && (
          <Button variant="ghost" size="sm" className="h-9 gap-1.5 text-slate-500"
                  onClick={() => { setStoreFilter(''); setDateFrom(''); setDateTo(''); }}
                  data-testid="orders-clear-filters">
            <FilterX className="w-3.5 h-3.5" /> Pulisci filtri
          </Button>
        )}
        <span className="text-xs text-slate-400 ml-auto" data-testid="orders-filtered-count">
          {filtered.length} di {(orders || []).length} ordini
        </span>
      </div>

      {canBulk && checked.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-slate-900 text-white px-4 py-2.5" data-testid="orders-bulk-bar">
          <span className="text-sm font-semibold" data-testid="orders-bulk-count">{checked.size} selezionati</span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <Button size="sm" variant="secondary" className="gap-1.5 rounded-none h-8" onClick={() => setShowEmail(true)} data-testid="bulk-email-btn">
              <Mail className="w-3.5 h-3.5" /> Invia mail
            </Button>
            <Button size="sm" variant="secondary" className="gap-1.5 rounded-none h-8" onClick={downloadPdf} disabled={busyPdf} data-testid="bulk-pdf-btn">
              {busyPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Scarica PDF
            </Button>
            {isOps && (
              <Button size="sm" variant="destructive" className="gap-1.5 rounded-none h-8" onClick={() => setConfirmDelete(true)} data-testid="bulk-delete-btn">
                <Trash2 className="w-3.5 h-3.5" /> Elimina
              </Button>
            )}
            <Button size="sm" variant="ghost" className="gap-1 rounded-none h-8 text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => setChecked(new Set())} data-testid="bulk-clear-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {orders === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 py-16 text-center text-slate-500" data-testid="orders-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Nessun ordine {filter ? 'con questo stato' : 'ancora'}.</p>
        </div>
      ) : (
        <div className="border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="orders-list">
          {filtered.map(o => {
            const meta = ORDER_STATUS_META[o.status] || ORDER_STATUS_META.pending;
            const waiting = overdueDays(o);
            return (
              <div key={o.order_id} className={`flex items-center ${checked.has(o.order_id) ? 'bg-blue-50/60' : ''}`}>
                {canBulk && (
                  <label className="pl-3 pr-1 py-3 cursor-pointer shrink-0" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={checked.has(o.order_id)} onChange={() => toggleCheck(o.order_id)}
                           className="w-4 h-4 accent-slate-900 block" data-testid={`order-check-${o.order_id}`} />
                  </label>
                )}
                <button
                  onClick={() => setSelected(o)}
                  data-testid={`order-row-${o.order_id}`}
                  className="flex-1 min-w-0 flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-mono text-sm font-semibold text-slate-900 w-24 shrink-0">{o.number}</span>
                  <span className={`px-2 py-0.5 text-[11px] font-semibold border shrink-0 ${meta.cls}`}>{meta.label}</span>
                  {waiting > 0 && (
                    <span className="px-2 py-0.5 text-[11px] font-semibold border bg-amber-100 text-amber-800 border-amber-400 shrink-0 inline-flex items-center gap-1"
                          title={`Approvato ${waiting} giorni fa, consegna non ancora registrata`}
                          data-testid={`order-overdue-badge-${o.order_id}`}>
                      <Clock className="w-3 h-3" /> {waiting} gg
                    </span>
                  )}
                  <span className="text-sm text-slate-700 min-w-0 flex-1 truncate">{o.store_name}</span>
                  <span className="text-xs text-slate-500 hidden sm:block">{o.requested_by_name}</span>
                  <span className="text-xs text-slate-500 hidden md:block">{fmtDate(o.created_at)}</span>
                  <span className="text-xs text-slate-500">{o.items_count} art.</span>
                  <span className="text-sm font-semibold text-slate-900 w-24 text-right">{fmtPrice(o.total)}</span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      <NewOrderDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        stores={stores}
        onCreated={() => { setShowNew(false); load(); }}
      />
      <OrderDetailDialog
        order={selected}
        onClose={() => setSelected(null)}
        onChanged={() => { setSelected(null); load(); }}
      />
      <EmailOrdersDialog
        open={showEmail}
        onClose={() => setShowEmail(false)}
        orderIds={checkedIds}
        onSent={() => setShowEmail(false)}
      />
      <OrdersSpendingReport open={showReport} onClose={() => setShowReport(false)} />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {checked.size} ordini?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;eliminazione è definitiva e rimuove gli ordini dallo storico. Gli strumenti già caricati a Magazzino non vengono toccati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="bulk-delete-confirm-btn">
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
